import { useRef, useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Label } from "@workspace/ui/components/label";
import { Textarea } from "@workspace/ui/components/textarea";

import { COMP_REASON_REQUIRED_MESSAGE } from "../comp";

/**
 * HA-658 slice 4 — the Comp reason prompt, for a line or the whole order (AC3: a reason is required). Server-confirmed (D-HA-24):
 * it closes once the server has applied the comp, and on a refusal stays open with the reason kept (the caller has said why). A
 * second press while the first is waiting is the same comp.
 */
export function CompDialog({
	open,
	title,
	description = "Mark it free. It is still made, and its stock still deducts.",
	onConfirm,
	onClose,
}: {
	open: boolean;
	title: string;
	description?: string;
	onConfirm: (reason: string) => void | Promise<unknown>;
	onClose: () => void;
}) {
	const [reason, setReason] = useState("");
	const [missing, setMissing] = useState(false);
	const [pending, setPending] = useState(false);
	const inFlight = useRef(false);

	const confirm = async () => {
		if (inFlight.current) return;
		const trimmed = reason.trim();
		if (!trimmed) {
			setMissing(true);
			return;
		}

		inFlight.current = true;
		setPending(true);
		try {
			await onConfirm(trimmed);
			setReason("");
			onClose();
		} catch {
			// Stay open: the comp didn't land, and the reason is still here to send again.
		} finally {
			inFlight.current = false;
			setPending(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={(next) => !next && !pending && onClose()}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
					<DialogDescription>{description}</DialogDescription>
				</DialogHeader>
				<div className="space-y-2">
					<Label htmlFor="comp-reason">Reason</Label>
					<Textarea
						id="comp-reason"
						value={reason}
						maxLength={500}
						placeholder="e.g. Staff meal, service recovery"
						onChange={(e) => {
							setReason(e.target.value);
							if (e.target.value.trim()) setMissing(false);
						}}
						aria-invalid={missing}
					/>
					{missing && (
						<p role="alert" className="text-sm text-destructive">
							{COMP_REASON_REQUIRED_MESSAGE}
						</p>
					)}
				</div>
				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={pending}>
						Cancel
					</Button>
					<Button onClick={() => void confirm()} disabled={pending}>
						{pending && <Loader2 className="mr-2 size-4 animate-spin" />}
						Comp
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
